// File: src/hooks/useActiveSection.js
import { useEffect, useState } from 'react';

/**
 * Narrative/ascent order of sections (home is the start of the journey,
 * contact is the end). This is the single source of truth for section
 * ordering across the app — do not redefine this list elsewhere. Where the
 * reverse (DOM/page top-to-bottom) order is needed, derive it with
 * `SECTION_ORDER.slice().reverse()`.
 */
export const SECTION_ORDER = ['home', 'about', 'experience', 'projects', 'certificates', 'contact'];

/**
 * Single source of truth for "what section is currently active/in view".
 *
 * Replaces two previously-separate, occasionally-inconsistent mechanisms:
 * the old pill nav's own local IntersectionObserver state, and App.js's
 * separate storage-backed section-index counter used to drive the
 * rocket's "next section" navigation.
 */
const useActiveSection = () => {
  const [activeId, setActiveId] = useState('home');

  useEffect(() => {
    // Tracks every observed section's current visibility ratio and always
    // picks whichever is most visible. A single fixed threshold (e.g. 0.6)
    // is never reached by sections taller than the viewport minus some
    // margin - on this site several sections exceed that, so their entries
    // never fire and `activeId` would freeze on the last section that did
    // cross the threshold. The multi-value threshold array below fires the
    // callback on every ~10% visibility change instead, so the "most
    // visible" comparison stays up to date continuously.
    const ratios = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          ratios.set(entry.target.id, entry.intersectionRatio);
        });

        let bestId = null;
        let bestRatio = 0;
        ratios.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });

        if (bestId) setActiveId(bestId);
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] }
    );

    SECTION_ORDER.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return activeId;
};

export default useActiveSection;
